import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Trophy, Medal } from "lucide-react";
import api from "@/lib/api";

function Pos({ n }) {
  return n <= 3
    ? <Medal className={n === 1 ? "text-[#D4AF37]" : n === 2 ? "text-gray-300" : "text-[#997a1a]"} />
    : <span>{n}</span>;
}

export default function CampionatoDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/public/championships/${id}`)
      .then(({ data }) => setData(data))
      .catch(() => setErr(true));
  }, [id]);

  if (err) return (
    <div className="py-32 text-center text-white">
      Campionato non trovato. <Link to="/campionati" className="cyber-text underline">Torna ai Campionati</Link>
    </div>
  );

  if (!data) return <div className="py-32 text-center text-gray-400">Caricamento...</div>;

  const c = data.championship;
  const archived = c.state === "archived";
  const clans = data.clan_ranking?.ranking || [];
  const mvps = data.mvp_ranking?.ranking || [];

  return (
    <div className="max-w-6xl mx-auto px-5 py-16 text-white">
      <Link to="/campionati" className="flex items-center gap-2 text-gray-400 mb-8">
        <ArrowLeft className="w-4 h-4" /> Tutti i Campionati
      </Link>

      <div className="cyber-panel clip-hud p-8 mb-12">
        <span className={`font-rajdhani text-xs px-2 py-1 ${
          archived ? "bg-[var(--gold)] text-black" : "bg-[#10b981] text-black"
        }`}>
          {archived ? "ARCHIVIATO" : "● LIVE"}
        </span>

        <h1 className={`heading text-5xl mt-3 ${archived ? "gold-text" : "cyber-text"}`}>
          {c.name}
        </h1>
      </div>

      <section className="mb-14">
        <h2 className="heading text-4xl mb-5">Tornei del Campionato</h2>
        {data.tournaments?.length ? (
          <div className="grid md:grid-cols-3 gap-4">
            {data.tournaments.map(t => (
              <Link key={t.id} to={`/tornei/${t.id}`} className="cyber-panel clip-hud p-5">
                <div className="heading text-2xl">{t.name}</div>
                <div className="cyber-text font-rajdhani text-sm mt-2">Visualizza Torneo →</div>
              </Link>
            ))}
          </div>
        ) : <p className="text-gray-500">Nessun torneo associato.</p>}
      </section>

      <section className="mb-14">
        <h2 className="heading text-4xl mb-5">
          Classifica Clan <span className={archived ? "gold-text" : "cyber-text"}>{archived ? "Finale" : "Live"}</span>
        </h2>

        <div className="cyber-panel clip-hud overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-white/10">
              <th className="p-3 text-left">Pos</th>
              <th className="p-3 text-left">Clan</th>
              <th className="p-3 text-right">Punti</th>
            </tr></thead>
            <tbody>
              {clans.map((r, i) => {
                const rank = r.rank ?? i + 1;
                return (
                  <tr key={r.clan_id || i} className="border-b border-white/5">
                    <td className="p-3"><Pos n={rank} /></td>
                    <td className="p-3">{r.clan_name || r.name || r.tag || "Clan"}</td>
                    <td className="p-3 text-right font-oswald text-xl cyber-text">{r.points ?? r.score ?? 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="heading text-4xl mb-5">
          Classifica MVP <span className={archived ? "gold-text" : "cyber-text"}>{archived ? "Finale" : "Live"}</span>
        </h2>

        <div className="panel panel-gold clip-hud overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-white/10">
              <th className="p-3 text-left">Pos</th>
              <th className="p-3 text-left">Player</th>
              <th className="p-3 text-right">Kill</th>
            </tr></thead>
            <tbody>
              {mvps.map((r, i) => {
                const rank = r.rank ?? i + 1;
                return (
                  <tr key={r.player_id || r.discord_id || i} className="border-b border-white/5">
                    <td className="p-3"><Pos n={rank} /></td>
                    <td className="p-3">{r.player_name || r.display_name || r.name || "Player"}</td>
                    <td className="p-3 text-right font-oswald text-xl gold-text">{r.kills ?? 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
